import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import '../PersonnelDialogStyle.scss';
import State from 'src/models/State';
import PagedResultSet from 'src/models/common/PagedResultSet';
import UpdateIRSCategoryPanel from './UpdateIRSCategoryPanel';
import ValidateDataPanel from './ValidateEntryPanel';
import useFetch from 'use-http';
import PersonnelTaxInformation from 'src/models/personnel/PersonnelTaxInformation';
import moment from 'moment';
import PersonnelToast from '../../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IPersonnelTaxInformationDialogProps {
    dialogName: string;
    selectedPersonnel: TSDILandman;
    stateInfo: PagedResultSet<State>;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    hideDialog: (location?: string) => void;
}

type DropDownSelection = {
    code: string,
    dscr: string
};

const PersonnelTaxInformationDialog: React.FC<IPersonnelTaxInformationDialogProps> = (props) => {
    const { handleSubmit, register, setValue, getValues, errors, reset } = useForm();
    const { get, put, response } = useFetch();

    const [showValidationPanel, setShowValidationPanel] = useState<boolean>(false);
    const [showIRSUpdatePanel, setShowIRSUpdatePanel] = useState<boolean>(false);

    const [irsCategoryTypes, setIrsCategoryTypes] = useState<Array<DropDownSelection>>(undefined);
    const [corporationTypes, setCorporationTypes] = useState<Array<DropDownSelection>>(undefined);
    const [personnelTaxInformation, setPersonnelTaxInformation] = useState<PersonnelTaxInformation>(undefined);

    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    const validationCategory = useRef<string>('');

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        resetForm();
        if (props.selectedPersonnel) {
            getTaxInformation();
        }
    }, [props.selectedPersonnel]);

    useEffect(() => {
        populateForm();
    }, [personnelTaxInformation]);

    const onCreate = () => {
        console.log('PersonnelTaxInfo onCreate()');
    };

    const onDestroy = () => {
        console.log('PersonnelTaxInfo onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // gets all tax information: personnel info, corporation types, and irs categories
    const getTaxInformation = async () => {
        let result = await get(`/v1.0/landmen/personnel/${props.selectedPersonnel?.tsdNo}/get-tax-information`);
        if (response.ok) {
            if (result) {
                if (result.landmanTaxInformation) {
                    setPersonnelTaxInformation(result.landmanTaxInformation);
                }
                if (result.corporationTypes) {
                    setCorporationTypes(result.corporationTypes.map((ct: DropDownSelection) => {
                        return { code: ct.code, dscr: ct.dscr };
                    }));
                }

                if (result.irsCategoryTypes) {
                    setIrsCategoryTypes(result.irsCategoryTypes.map((ct: DropDownSelection) => {
                        return { code: ct.code, dscr: ct.dscr };
                    }));
                }
            }
        } else {
            props.showToast('Unable to get selected personnel\'s tax information', `rgba(255,0,0,0.5)`);
        }
    };

    // maps corporation types for select list
    const corporationTypeDropdown = () => {
        return corporationTypes
            ? corporationTypes?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    const populateForm = () => {
        const values = {
            useSsn: personnelTaxInformation?.useSsn ?? '',
            irsCat: personnelTaxInformation?.taxType ?? '',
            corpType: personnelTaxInformation?.corpType ?? '',
            corpTypeDate: personnelTaxInformation?.corpTypeDate ? (moment(personnelTaxInformation?.corpTypeDate).format('YYYY-MM-DD') as unknown as Date) : null,
            incorporatedDate: personnelTaxInformation?.incorporatedDate ? (moment(personnelTaxInformation?.incorporatedDate).format('YYYY-MM-DD') as unknown as Date) : null,
            effDate: personnelTaxInformation?.effDate ? (moment(personnelTaxInformation?.effDate).format('YYYY-MM-DD') as unknown as Date) : null,
            birthDate: personnelTaxInformation?.birthDate ? (moment(personnelTaxInformation?.birthDate).format('YYYY-MM-DD') as unknown as Date) : null,
            cplNo: personnelTaxInformation?.cplNo ?? '',
            aaplNo: personnelTaxInformation?.aaplNo ?? '',

            ssn: personnelTaxInformation?.ssn ? formatSsn(personnelTaxInformation?.ssn) : '',
            federalTaxId: personnelTaxInformation?.federalTaxId ? formatTaxId(personnelTaxInformation?.federalTaxId) : '',

            // taxAddr1: personnelTaxInformation?.taxAddr1 ?? '',
            // taxAddr2: personnelTaxInformation?.taxAddr2 ?? '',
            // taxZip1: personnelTaxInformation?.taxZip1 ?? '',
            // taxZip2: personnelTaxInformation?.taxZip2 ?? '',
            // taxCity: personnelTaxInformation?.taxCity ?? '',
            // taxState: personnelTaxInformation?.taxState ?? '',

            chkDest: personnelTaxInformation?.chkDest ?? '',
            // chkAddr1: personnelTaxInformation?.chkAddr1 ?? '',
            // chkAddr2: personnelTaxInformation?.chkAddr2 ?? '',
            // chkZip1: personnelTaxInformation?.chkZip1 ?? '',
            // chkZip2: personnelTaxInformation?.chkZip2 ?? '',
            // chkCity: personnelTaxInformation?.chkCity ?? '',
            // chkState: personnelTaxInformation?.chkState ?? '',

            bankRouting: personnelTaxInformation?.bankRouting ?? '',
            bankAcctNo: personnelTaxInformation?.bankAcctNo ?? '',
            bankAcctType: personnelTaxInformation?.bankAcctType ?? '',

            externalEmployee: personnelTaxInformation?.externalEmployee ?? false
        };

        reset(values);
    };

    const formatSsn = (ssn: string) => {
        let strippedSsn = ssn.replace(/[^0-9.]/g, '');

        strippedSsn = strippedSsn
            .replace(/^(\d{3})(\d{0,2})(\d{0,4}).*/, (_, p1, p2, p3) => {
                let result = p1;
                if (p2) {
                    result += `-${p2}`;
                }

                if (p3) {
                    result += `-${p3}`;
                }
                return result;
            });

        return strippedSsn;
    };

    const formatTaxId = (taxId: string) => {
        let strippedTaxId = taxId.replace(/[^0-9.]/g, '');
        strippedTaxId = strippedTaxId
            .replace(/^(\d{2})(\d{0,7}).*/, (_, p1, p2) => {
                let result = p1;
                if (p2) {
                    result += `-${p2}`;
                }
                return result;
            });

        return strippedTaxId;
    };

    const resetForm = () => {
        const values = {
            useSsn: '',
            irsCat: '',
            corpType: '',
            corpTypeDate: '',
            incorporatedDate: '',
            birthDate: '',
            cplNo: '',
            aaplNo: '',

            ssn: '',
            federalTaxId: '',

            // taxAddr1: '',
            // taxAddr2: '',
            // taxZip1: '',
            // taxZip2: '',
            // taxCity: '',
            // taxState: '',

            chkDest: '',
            // chkAddr1: '',
            // chkAddr2: '',
            // chkZip1: '',
            // chkZip2: '',
            // chkCity: '',
            // chkState: '',

            bankRouting: '',
            bankAcctNo: '',
            bankAcctType: '',

            externalEmployee: false
        };

        reset(values);
    };

    // start dialogs
    const showIRSCategoryDialog = () => {
        setShowIRSUpdatePanel(true);
    };

    const showValidateDataDialog = (category: string) => {
        validationCategory.current = category;
        setShowValidationPanel(true);
    };

    const hideDialog = (updatedCategory?: string, newEffDate?: Date) => {
        if (updatedCategory !== null && updatedCategory !== '') {
            let irsLabel = irsCategoryTypes.find((cat) => cat.code === updatedCategory)?.dscr;
            setValue('irsCat', irsLabel);

            let formattedEffDate = newEffDate ? (moment(newEffDate).format('YYYY-MM-DD') as unknown as Date) : null;
            setValue('effDate', formattedEffDate);
            showToast('IRS Category updated successfully');
        }
        setShowValidationPanel(false);
        setShowIRSUpdatePanel(false);
    };
    // end dialogs

    const submitValidatedData = (val: string) => {
        switch (validationCategory.current) {
            case 'Social Security Number':
                setValue('ssn', val);
                break;
            case 'Tax Id Number':
                setValue('federalTaxId', val);
                break;
            case 'Bank Account Number':
                setValue('bankAcctNo', val);
                break;
            case 'Bank Routing Number':
                setValue('bankRouting', val);
                break;
            default:
                showToast('bad data - unable to set validated data for category: ' + validationCategory.current, `rgba(255,0,0,0.5)`);
        }
    };

    // const stateDropdownOptions = () => {
    //     return props.stateInfo
    //         ? props.stateInfo?.items?.map((st: State) => {
    //             return { value: st.abbreviation, label: st.name };
    //         })
    //         : [];
    // };

    // const copyAddress = (section: string) => {
    //     let selectedPersonnel = props.selectedPersonnel;
    //     if (!!selectedPersonnel?.addr1) {
    //         setValue(`${section}Addr1`, selectedPersonnel.addr1 ? selectedPersonnel.addr1 : '');
    //         setValue(`${section}Addr2`, selectedPersonnel.addr2 ? selectedPersonnel.addr2 : '');
    //         setValue(`${section}Zip`, selectedPersonnel.zip1 ? selectedPersonnel.zip1 : '');
    //         setValue(`${section}City`, selectedPersonnel.city ? selectedPersonnel.city : '');
    //         setValue(`${section}State`, selectedPersonnel.state ? selectedPersonnel.state : ''); // TODO need to convert state name to map from LMS
    //     } else if (!!selectedPersonnel?.offAddr1) {
    //         setValue(`${section}Addr1`, selectedPersonnel.offAddr1 ? selectedPersonnel.offAddr1 : '');
    //         setValue(`${section}Addr2`, selectedPersonnel.offAddr2 ? selectedPersonnel.offAddr2 : '');
    //         setValue(`${section}Zip`, selectedPersonnel.offZip1 ? selectedPersonnel.offZip1 : '');
    //         setValue(`${section}City`, selectedPersonnel.city ? selectedPersonnel.city : '');
    //         setValue(`${section}State`, selectedPersonnel.state ? selectedPersonnel.state : '');
    //     } else {
    //         showToast('No mailing or corporate address found for: ' + selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName, `rgba(255,0,0,0.5)`);
    //     }
    // };

    const onSubmit = async (data: PersonnelTaxInformation) => {
        data.corpTypeDate = new Date(getValues('corpTypeDate'));
        data.incorporatedDate = new Date(getValues('incorporatedDate'));
        data.effDate = new Date(getValues('effDate'));
        data.birthDate = new Date(getValues('birthDate'));

        data.bankRouting = data.bankRouting ? parseInt(data.bankRouting.toString(), 10) : null;
        data.ssn = data.ssn ? data.ssn.replace(/[^0-9.]/g, '') : null;
        data.federalTaxId = data.federalTaxId ? data.federalTaxId.replace(/[^0-9.]/g, '') : null;

        data.chkState = data.chkState ? data.chkState : null;
        data.taxState = data.taxState ? data.taxState : null;

        await put(`/v1.0/landmen/personnel/${props.selectedPersonnel?.tsdNo}/update-tax-information`, data);
        if (response.ok) {
            getTaxInformation();
            props.showToast('Tax information updated successfully');
            props.hideDialog('tax-information');
        } else {
            showToast('Unable to update tax information: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    return (
        <Dialog title="Tax Information / Checking" dialogName={props.dialogName} bodyStyle={{ width: 'auto', minWidth: '60%', height: 'auto', maxHeight: '85%', overflow: 'auto' }}>
            <div style={{ position: 'relative' }}>
                <div>
                    {toast && (
                        <PersonnelToast
                            message={toast.message}
                            color={toast.color}
                            onClose={() => setToast(null)}
                            persistent={toast.persistent}
                        />
                    )}
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="dialog-two-col">
                        <div className="dialog-col">
                            <div className="dialog-col-top">
                                <h3>Tax Information</h3>
                                <div className="dialog-form-row" style={{ justifyContent: 'right' }}>
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="ssn"
                                        defaultVal={props.selectedPersonnel?.ssn}
                                        label="Social Security #"
                                        register={register}
                                        errors={errors}
                                        disabled={true}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                        labelStyle={{ textAlign: 'right' }}
                                    />
                                    <a onClick={() => showValidateDataDialog('Social Security Number')}>
                                        <i style={{ marginLeft: 'auto', fontSize: '1.5rem' }} className="far fa-edit mr-1 clickable" />
                                    </a>
                                </div>
                                <div className="dialog-form-row" style={{ justifyContent: 'right' }}>
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="federalTaxId"
                                        defaultVal={props.selectedPersonnel?.taxId}
                                        label="Tax Id. Number"
                                        register={register}
                                        errors={errors}
                                        disabled={true}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                        labelStyle={{ textAlign: 'right' }}
                                    />
                                    <a onClick={() => showValidateDataDialog('Tax Id Number')}>
                                        <i style={{ marginLeft: 'auto', fontSize: '1.5rem' }} className="far fa-edit mr-1 clickable" />
                                    </a>
                                </div>
                                {/* <RHFSelect
                                    bodyStyle={{ width: 'auto', marginBottom: '1rem' }}
                                    labelStyle={{ textAlign: 'right' }}
                                    name="useSsn"
                                    label="Use SSN or EIN"
                                    options={[
                                        { value: 'N', label: 'Federal Tax ID (EIN)' },
                                        { value: 'Y', label: 'Social Security Number (SSN)' },
                                    ]}
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                /> */}
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="irsCat"
                                    label="IRS Category Type"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                    disabled={true}
                                />
                                <div className="dialog-form-row" style={{ justifyContent: 'right' }}>
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="effDate"
                                        label="Effective Date"
                                        type="date"
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                    <a onClick={showIRSCategoryDialog}>
                                        <i style={{ marginLeft: 'auto', fontSize: '1.5rem' }} className="far fa-edit mr-1 clickable" />
                                    </a>
                                </div>
                                <RHFSelect
                                    bodyStyle={{ width: 'auto', marginBottom: '1rem' }}
                                    labelStyle={{ textAlign: 'right' }}
                                    name="corpType"
                                    label="Corportation Type"
                                    options={corporationTypeDropdown()}
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="corpTypeDate"
                                    label="Corp. Type Date"
                                    type="date"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                {/* <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="incorporatedDate"
                                    label="Incorporated Date"
                                    type="date"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                /> */}
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="birthDate"
                                    label="Date of Birth"
                                    type="date"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                {/* <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="cplNo"
                                    label="CPL Number"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                /> */}
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="aaplNo"
                                    label="AAPL Number"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                            </div>

                            {/* <hr style={{ width: '100%' }} /> */}

                            {/* <span>
                                <h3 style={{ marginTop: '2rem' }}>W-9 / 1099 Address Information
                                    <a onClick={() => copyAddress('tax')}>
                                        <i style={{ marginLeft: '5px', fontSize: '1.7rem' }} className="far fa-copy mr-1 clickable" />
                                    </a>
                                </h3>
                            </span>
                            <div className="dialog-col-bottom">
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="taxAddr1"
                                    label="Address Line 1"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="taxAddr2"
                                    label="Address Line 2"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                <div className="dialog-form-row">
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="taxZip1"
                                        label="Zip Code"
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                    -
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="taxZip2"
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                                <div className="dialog-form-row">
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="taxCity"
                                        label="City"
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                    <RHFSelect
                                        bodyStyle={{ width: 'auto', flex: 1.5 }}
                                        labelStyle={{ textAlign: 'right' }}
                                        style={{ width: 'auto', paddingLeft: '2px' }}
                                        name="taxState"
                                        label="State"
                                        options={stateDropdownOptions()}
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                </div>
                            </div> */}
                        </div>

                        <div className="vertical-divider" />

                        <div className="dialog-col">
                            <div className="dialog-col-top" style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, alignContent: 'top' }}>
                                <span>
                                    <h3 style={{ marginTop: '2rem' }}>Send Check To
                                        {/* <a onClick={() => copyAddress('chk')}>
                                            <i style={{ marginLeft: '5px', fontSize: '1.7rem' }} className="far fa-copy mr-1 clickable" />
                                        </a> */}
                                    </h3>
                                </span>
                                <RHFSelect
                                    bodyStyle={{ width: 'auto', marginBottom: '1rem' }}
                                    labelStyle={{ textAlign: 'right' }}
                                    name="chkDest"
                                    label="Send To"
                                    options={[
                                        { value: 'N', label: 'Company Office' },
                                        { value: 'D', label: 'Direct Deposit' },
                                        { value: 'H', label: 'Home' },
                                        { value: 'O', label: 'Mailing Address' },
                                        { value: 'Y', label: 'Other' },
                                    ]}
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                {/* <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="chkAddr1"
                                    label="Address Line 1"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                <RHFInputBox
                                    style={{ width: 'auto', marginBottom: '1rem' }}
                                    name="chkAddr2"
                                    label="Address Line 2"
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    spaceLabelContent={true}
                                />
                                <div className="dialog-form-row">
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="chkZip1"
                                        label="Zip Code"
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                    -
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="chkZip2"
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                                <div className="dialog-form-row">
                                    <RHFInputBox
                                        style={{ width: 'auto', marginBottom: '1rem' }}
                                        name="chkCity"
                                        label="City"
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                    <RHFSelect
                                        bodyStyle={{ width: 'auto', flex: 1.5 }}
                                        labelStyle={{ textAlign: 'right' }}
                                        style={{ width: 'auto' }}
                                        name="chkState"
                                        label="State"
                                        options={stateDropdownOptions()}
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                </div> */}
                                <div style={{ marginTop: 'auto' }}>
                                    <div className="dialog-form-row" style={{ justifyContent: 'right' }}>
                                        <RHFInputBox
                                            style={{ width: 'auto', marginBottom: '1rem' }}
                                            name="bankRouting"
                                            label="Routing No."
                                            register={register}
                                            disabled={true}
                                            errors={errors}
                                            labelOnLeft={true}
                                            spaceLabelContent={true}
                                        />
                                        <a onClick={() => showValidateDataDialog('Bank Routing Number')}>
                                            <i style={{ marginLeft: 'auto', fontSize: '1.5rem' }} className="far fa-edit mr-1 clickable" />
                                        </a>
                                    </div>

                                    <div className="dialog-form-row" style={{ justifyContent: 'right' }}>
                                        <RHFInputBox
                                            style={{ width: 'auto', marginBottom: '1rem' }}
                                            name="bankAcctNo"
                                            label="Acct No."
                                            disabled={true}
                                            register={register}
                                            errors={errors}
                                            labelOnLeft={true}
                                            spaceLabelContent={true}
                                        />
                                        <a onClick={() => showValidateDataDialog('Bank Account Number')}>
                                            <i style={{ marginLeft: 'auto', fontSize: '1.5rem' }} className="far fa-edit mr-1 clickable" />
                                        </a>
                                    </div>
                                    <RHFSelect
                                        bodyStyle={{ width: 'auto', flex: 1.5 }}
                                        style={{ width: 'auto' }}
                                        labelStyle={{ textAlign: 'right' }}
                                        name="bankAcctType"
                                        label="Acct Type"
                                        options={[
                                            { value: 'C', label: 'Checking' },
                                            { value: 'S', label: 'Savings' },
                                        ]}
                                        register={register}
                                        errors={errors}
                                        labelOnLeft={true}
                                        spaceLabelContent={true}
                                    />
                                </div>
                            </div>

                            <hr style={{ width: '100%' }} />

                            <h3 style={{ marginTop: '2rem' }}>&#x200b;</h3>
                            <div className="dialog-col-bottom">
                                <RHFCheckbox
                                    name="externalEmployee"
                                    label="External Company"
                                    style={{ verticalAlign: 'middle' }}
                                    register={register}
                                    errors={errors}
                                    labelOnLeft={true}
                                    disabled={true}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="button-row">
                        <Button className="blue-hover-button" style={{ width: '24%' }} text={'Save'} type="submit" intent="primary" />
                        <Button
                            className="blue-hover-button"
                            style={{ width: '24%' }}
                            text={'Close'}
                            onClick={() => {
                                props.hideDialog();
                                setShowValidationPanel(false);
                                setShowIRSUpdatePanel(false);
                            }}
                            intent="secondary"
                        />
                    </div>

                </form>
                {(showIRSUpdatePanel || showValidationPanel) && <div className="popup-overlay">
                    {showIRSUpdatePanel && (
                        <div className="popup-overlay-panel">
                            <UpdateIRSCategoryPanel
                                closePanel={hideDialog}
                                irsDropdownOptions={irsCategoryTypes}
                                currentIrsCat={personnelTaxInformation?.taxType}
                                selectedPersonnel={props.selectedPersonnel}
                            />
                        </div>
                    )}
                    {showValidationPanel && (
                        <div className="popup-overlay-panel">
                            <ValidateDataPanel category={validationCategory.current} closePanel={() => hideDialog('')} submitValue={submitValidatedData} />
                        </div>
                    )}
                </div>}
            </div>
        </Dialog>
    );
};

export default PersonnelTaxInformationDialog;


// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/taxInfo/PersonnelTaxInformationDialog.tsx